import { useQuery, type ApolloError } from "@apollo/client";
import { useMemo } from "react";
import {
  GetOrdersListCountDocument,
  GetOrdersListDocument,
  GetOrdersListSummaryDocument,
  type OrdersListFilterInput,
  type OrdersListOrderFragment,
  type OrdersSortV2,
} from "../../gql/generated/graphql";
import {
  buildMaterialAndShadeCatalog,
  formatProduct,
  type MaterialAndShadeCatalog,
} from "./formatProduct";

interface Props {
  dentalOfficeId: string | null;
  filter?: OrdersListFilterInput;
  sort?: OrdersSortV2;
  pageSize: number;
  page?: number;
  // The total is a query of its own, only made when the page shows it
  withTotalCount?: boolean;
  skip?: boolean;
}

const formatOrdersListOrder = ({
  products,
  ...order
}: OrdersListOrderFragment) => ({
  ...order,
  products: products.map(formatProduct),
});

export type OrdersListOrder = ReturnType<typeof formatOrdersListOrder>;

// Named so that the pages returning it do not reach for the generated GraphQL types (TS2742)
export interface OrdersList {
  orders: OrdersListOrder[];
  materialAndShadeCatalog: MaterialAndShadeCatalog;
  totalCount?: number;
  // The orders are those of the previous variables, while the asked page or filter loads
  isPreviousPage: boolean;
  loading: boolean;
  error?: ApolloError;
}

// A page of the orders of a dental office (drafts left out), with the catalog naming their materials and shades
export const useOrdersList = ({
  dentalOfficeId,
  filter,
  sort,
  pageSize,
  page = 1,
  withTotalCount = false,
  skip = false,
}: Props): OrdersList => {
  const { data, previousData, loading, error } = useQuery(
    GetOrdersListDocument,
    {
      variables: {
        dentalOfficeId,
        filter,
        sort,
        take: pageSize,
        skip: pageSize * (page - 1),
      },
      skip,
      fetchPolicy: "cache-and-network",
      // A page or a filter already seen comes back from the cache, without emptying the list
      nextFetchPolicy: () => "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = useQuery(
    GetOrdersListCountDocument,
    {
      variables: { dentalOfficeId, filter },
      skip: skip || !withTotalCount,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: () => "cache-first",
    }
  );

  // While a new page loads, the previous one stays rather than emptying the list
  const orders = (data ?? previousData)?.getOrdersListByDentalOfficeId;
  const totalCount = (countData ?? previousCountData)
    ?.getOrdersListCountByDentalOfficeId;

  return useMemo(
    () => ({
      orders: (orders ?? []).map(formatOrdersListOrder),
      materialAndShadeCatalog: buildMaterialAndShadeCatalog(
        (orders ?? []).flatMap(({ products }) => products)
      ),
      totalCount,
      isPreviousPage: !data && !!previousData,
      loading,
      error,
    }),
    [orders, totalCount, data, previousData, loading, error]
  );
};

// The figures of an orders list header: every order of the dental office, and those waiting for the dentist
export const useOrdersListSummary = ({
  dentalOfficeId,
  skip = false,
}: Pick<Props, "dentalOfficeId" | "skip">) => {
  const { data } = useQuery(GetOrdersListSummaryDocument, {
    variables: { dentalOfficeId },
    skip,
    fetchPolicy: "cache-and-network",
  });

  return {
    total: data?.total,
    awaitingAction: data?.awaitingAction,
  };
};
